import moment, { Moment } from 'moment-timezone'
import { OccasionalSlot, OccasionalSlotPriorityGroup } from '@/types/Slots'
import { DaycareType } from '@/types/Daycare'
import { Role } from '@/types/Role'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'

interface GroupedOccasionalSlot {
    key: string
    slots: OccasionalSlot[]
}

export function dataOccasionalSlots(
    occasionalSlots: OccasionalSlot[],
    datesClosingDays: Moment[],
    daycareType: DaycareType,
    role?: Role
): EventCalendar[] {
    if (role === Role.parent) {
        return formatParentOccasionalSlots(occasionalSlots, datesClosingDays)
    } else if (daycareType === DaycareType.rpe || daycareType === DaycareType.laep) {
        return formatRPEOccasionalSlots(occasionalSlots, datesClosingDays)
    } else {
        return formatOccasionalSlots(occasionalSlots, datesClosingDays)
    }
}

function formatParentOccasionalSlots(occasionalSlots: OccasionalSlot[], datesClosingDays: Moment[]): EventCalendar[] {
    const groupedSlotsMap: { [key: string]: GroupedOccasionalSlot } = {}

    occasionalSlots.forEach((slot: OccasionalSlot): void => {
        const start: string = moment(slot.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(slot.end, 'HH:mm:ss').format('HH:mm')
        const date: string = moment(slot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        const key: string = `${date}-${start}-${end}
							 ${slot.placeId ? '-' + slot.placeId : ''}
							 ${slot.childId ? '-' + slot.childId : ''}
							 ${slot.externalNote ? '-' + slot.externalNote : ''}
							 ${slot.externalIcon ? '-' + slot.externalIcon : ''}`

        if (!groupedSlotsMap[key]) {
            groupedSlotsMap[key] = { key, slots: [] }
        }
        groupedSlotsMap[key].slots.push(slot)
    })

    return Object.values(groupedSlotsMap)
        .filter((group: GroupedOccasionalSlot): boolean => {
            const { slots } = group
            const firstSlot: OccasionalSlot = slots[0]

            return !datesClosingDays.find((date: Moment): boolean => date.isSame(moment(firstSlot.date, 'DD-MM-YYYY')))
        })
        .map((group: GroupedOccasionalSlot): EventCalendar => {
            const { slots } = group
            const firstSlot: OccasionalSlot = slots[0]

            const date: string = moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
            const start: string = moment(firstSlot.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(firstSlot.end, 'HH:mm:ss').format('HH:mm')

            const title: string =
                (firstSlot.childId !== null ? `${firstSlot.childName ? ' - ' + firstSlot.childName : ''}` : '') +
                `${firstSlot.placeName ? ' - ' + firstSlot.placeName : ''}`

            return {
                title: `${start} - ${end} ${title}`,
                start: moment(date + ' ' + start, 'YYYY-MM-DD HH:mm').toDate(),
                end: moment(date + ' ' + end, 'YYYY-MM-DD HH:mm').toDate(),
                number: slots.length > 1 ? `${slots.length}` : '',
                type: firstSlot.type,
                childId: firstSlot.childId ? firstSlot.childId : undefined,
                sectionId: firstSlot.sectionId ? firstSlot.sectionId : undefined,
                placeId: firstSlot.placeId ? firstSlot.placeId : undefined,
                slots: slots
            }
        })
}

function formatRPEOccasionalSlots(occasionalSlots: OccasionalSlot[], datesClosingDays: Moment[]): EventCalendar[] {
    const groupedSlotsMap: { [key: string]: GroupedOccasionalSlot } = {}

    occasionalSlots.forEach((slot: OccasionalSlot): void => {
        const start: string = moment(slot.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(slot.end, 'HH:mm:ss').format('HH:mm')
        const date: string = moment(slot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        const key: string = `${date}-${start}-${end}
							 ${slot.placeId ? '-' + slot.placeId : ''}
							 ${slot.parentId ? '-' + slot.parentId : slot.childId ? '-' + slot.childId : ''}
							 ${slot.externalNote ? '-' + slot.externalNote : ''}
							 ${slot.externalIcon ? '-' + slot.externalIcon : ''}
							 ${slot.internalNote ? '-' + slot.internalNote : ''}
							 ${slot.sectionId ? '-' + slot.sectionId : ''}
							 ${
                                 slot.priorityGroup
                                     ? '-' +
                                       [...slot.priorityGroup]
                                           .sort((a, b) => a.id - b.id)
                                           .flatMap((pg) => pg.id)
                                           .join('-')
                                     : ''
                             }
							${slot.type}`

        if (!groupedSlotsMap[key]) {
            groupedSlotsMap[key] = { key, slots: [] }
        }
        groupedSlotsMap[key].slots.push(slot)
    })

    return Object.values(groupedSlotsMap)
        .filter((group: GroupedOccasionalSlot): boolean => {
            const { slots } = group
            const firstSlot: OccasionalSlot = slots[0]

            return !datesClosingDays.find((date: Moment): boolean => date.isSame(moment(firstSlot.date, 'DD-MM-YYYY')))
        })
        .map((group: GroupedOccasionalSlot): EventCalendar => {
            const { slots } = group
            const firstSlot: OccasionalSlot = slots[0]

            const start: string = moment(firstSlot.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(firstSlot.end, 'HH:mm:ss').format('HH:mm')

            const title: string = firstSlot.parentId
                ? `${
                      firstSlot.childId !== null
                          ? `${firstSlot.parentName ? ' - ' + firstSlot.parentName : ''}`
                          : `${firstSlot.section ? ' - ' + firstSlot.section : ''}`
                  } ${firstSlot.placeName ? ' - ' + firstSlot.placeName : ''}`
                : `${
                      firstSlot.childId !== null
                          ? `${firstSlot.childName ? ' - ' + firstSlot.childName : ''}`
                          : `${firstSlot.section ? ' - ' + firstSlot.section : ''}`
                  } ${firstSlot.placeName ? ' - ' + firstSlot.placeName : ''}`

            return buildOccasionalSlots(start, end, title, firstSlot, slots)
        })
}

function formatOccasionalSlots(occasionalSlots: OccasionalSlot[], datesClosingDays: Moment[]): EventCalendar[] {
    const groupedSlotsMap: { [key: string]: GroupedOccasionalSlot } = {}

    occasionalSlots.forEach((slot: OccasionalSlot): void => {
        const start: string = moment(slot.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(slot.end, 'HH:mm:ss').format('HH:mm')
        const date: string = moment(slot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        const key: string = `${date}-${start}-${end}
							 ${slot.placeId ? '-' + slot.placeId : ''}
							 ${slot.childId ? '-' + slot.childId : ''}
							 ${slot.externalNote ? '-' + slot.externalNote : ''}
							 ${slot.externalIcon ? '-' + slot.externalIcon : ''}
							 ${slot.internalNote ? '-' + slot.internalNote : ''}
							 ${slot.sectionId ? '-' + slot.sectionId : ''}
							 ${
                                 slot.priorityGroup
                                     ? '-' +
                                       [...slot.priorityGroup]
                                           .sort(
                                               (a: OccasionalSlotPriorityGroup, b: OccasionalSlotPriorityGroup) =>
                                                   a.id - b.id
                                           )
                                           .flatMap((pg: OccasionalSlotPriorityGroup) => pg.id)
                                           .join('-')
                                     : ''
                             }
							${slot.type}
							${slot.scheduled && slot.type === CalendarEventType.SCHEDULED ? '-' + moment(slot.scheduled, 'DD-MM-YYYY').format('YYYY-MM-DD') : ''}
                            ${slot.expiry ? '-' + moment(slot.expiry, 'DD-MM-YYYY').format('YYYY-MM-DD') : ''}`

        if (!groupedSlotsMap[key]) {
            groupedSlotsMap[key] = { key, slots: [] }
        }
        groupedSlotsMap[key].slots.push(slot)
    })

    return Object.values(groupedSlotsMap)
        .filter((group: GroupedOccasionalSlot): boolean => {
            const { slots } = group
            const firstSlot: OccasionalSlot = slots[0]

            return !datesClosingDays.find((date: Moment) => date.isSame(moment(firstSlot.date, 'DD-MM-YYYY')))
        })
        .map((group: GroupedOccasionalSlot): EventCalendar => {
            const { slots } = group
            const firstSlot: OccasionalSlot = slots[0]

            const start: string = moment(firstSlot.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(firstSlot.end, 'HH:mm:ss').format('HH:mm')

            const title: string =
                firstSlot.childId !== null
                    ? `${firstSlot.childName ? ' - ' + firstSlot.childName : ''}
					 ${firstSlot.childComplement ? ' - ' + firstSlot.childComplement : ''}
					 ${firstSlot.section ? ' - ' + firstSlot.section : ''}
					 ${firstSlot.placeName ? ' - ' + firstSlot.placeName : ''}`
                    : `${firstSlot.section ? ' - ' + firstSlot.section : ''} 
			${firstSlot.placeName ? ' - ' + firstSlot.placeName : ''}`

            return buildOccasionalSlots(start, end, title, firstSlot, slots)
        })
}

function buildOccasionalSlots(
    start: string,
    end: string,
    title: string,
    firstSlot: OccasionalSlot,
    slots: OccasionalSlot[]
): EventCalendar {
    return {
        title: `${start} - ${end} ${title}`,
        start: moment(
            moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + start,
            'YYYY-MM-DD HH:mm'
        ).toDate(),
        end: moment(moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + end, 'YYYY-MM-DD HH:mm').toDate(),
        number: slots.length > 1 ? `${slots.length}` : '',
        type: firstSlot.type,
        childId: firstSlot.childId ? firstSlot.childId : undefined,
        sectionId: firstSlot.sectionId ? firstSlot.sectionId : undefined,
        placeId: firstSlot.placeId ? firstSlot.placeId : undefined,
        slots: slots
    }
}
